import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  FaShieldAlt, 
  FaClock, 
  FaTools, 
  FaUsers, 
  FaAward, 
  FaHandshake,
  FaCheckCircle 
} from 'react-icons/fa';
import DeleteButton from '../editors/DeleteButton';

interface WhyChooseUsReason {
  icon: any;
  title: string;
  description: string;
  color: string;
  bgColor: string;
}

interface WhyChooseUsProps {
  language?: 'ar' | 'en';
  reasons?: WhyChooseUsReason[];
  onDeleteReason?: (index: number) => void;
}

const WhyChooseUs: React.FC<WhyChooseUsProps> = ({ 
  language = 'ar',
  reasons = [
    {
      icon: FaShieldAlt,
      title: 'الجودة والسلامة',
      description: 'نلتزم بأعلى معايير الجودة والسلامة في جميع مشاريعنا، مع شهادات معتمدة عالمياً',
      color: 'from-green-500 to-green-600',
      bgColor: 'bg-green-50'
    },
    {
      icon: FaClock,
      title: 'الالتزام بالمواعيد',
      description: 'نحترم مواعيدنا ونلتزم بتسليم المشاريع في الوقت المحدد دون تأخير',
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      icon: FaTools,
      title: 'أحدث التقنيات',
      description: 'نستخدم أحدث التقنيات والمواد في جميع مراحل البناء والتشييد',
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-50'
    },
    {
      icon: FaUsers,
      title: 'فريق محترف',
      description: 'فريق من المهندسين والفنيين ذوي الخبرة العالية والكفاءة المثبتة',
      color: 'from-orange-500 to-orange-600',
      bgColor: 'bg-orange-50'
    },
    {
      icon: FaAward,
      title: 'خبرة 11+ سنة',
      description: 'خبرة عريقة تزيد عن 11 عاماً في مجال البناء والتشييد والمحاجر',
      color: 'from-red-500 to-red-600',
      bgColor: 'bg-red-50'
    },
    {
      icon: FaHandshake,
      title: 'شراكة دائمة',
      description: 'نبني علاقات طويلة المدى مع عملائنا ونقدم خدمة ما بعد البيع ممتازة',
      color: 'from-teal-500 to-teal-600',
      bgColor: 'bg-teal-50'
    }
  ],
  onDeleteReason
}) => {
  const isRTL = language === 'ar';

  const features = language === 'ar'
    ? [
        'تصميم معماري متميز',
        'مواد بناء عالية الجودة',
        'فريق عمل محترف',
        'ضمان الجودة',
        'خدمة عملاء 24/7',
        'أسعار تنافسية',
        'التزام بالمواعيد',
        'شهادات معتمدة'
      ]
    : [
        'Outstanding architectural design',
        'High-quality building materials',
        'Professional team',
        'Quality assurance',
        '24/7 customer service',
        'Competitive prices',
        'On-time delivery',
        'Certified credentials'
      ];

  return (
    <section className="section-padding bg-white">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="mb-6">
              <motion.span
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className="inline-block px-4 py-2 bg-primary-100 text-primary-700 rounded-full text-sm font-medium mb-4"
              >
                {language === 'ar' ? 'ما يميزنا' : 'Why Choose Us'}
              </motion.span>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.5 }}
                className="text-3xl md:text-4xl font-bold text-gray-900 mb-4"
              >
                {language === 'ar' ? 'نتميز بالخبرة والجودة العالية' : 'Distinguished by Experience and High Quality'}
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.5 }}
                className="text-lg text-gray-600 leading-relaxed"
              >
                {language === 'ar' 
                  ? 'شركة مكة البركة للمقاولات العامة هي الخيار الأمثل لجميع احتياجاتك في مجال البناء والتشييد. نتميز بخبرة عريقة وسمعة طيبة في السوق المصري والعربي.'
                  : 'Mecca El Baraka General Contracting Company is the optimal choice for all your needs in the field of construction and building. We are distinguished by long-standing experience and a good reputation in the Egyptian and Arab markets.'
                }
              </motion.p>
            </div>

            {/* Features List */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-3">
                {features.slice(0, 4).map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 + index * 0.1, duration: 0.5 }}
                    className="flex items-center space-x-3 rtl:space-x-reverse"
                  >
                    <FaCheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-gray-700">{feature}</span>
                  </motion.div>
                ))}
              </div>
              <div className="space-y-3">
                {features.slice(4).map((feature, index) => (
                  <motion.div
                    key={index + 4}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 + (index + 4) * 0.1, duration: 0.5 }}
                    className="flex items-center space-x-3 rtl:space-x-reverse"
                  >
                    <FaCheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-gray-700">{feature}</span>
                  </motion.div>
                ))}
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.5 }}
              className="mt-8"
            >
              <Link
                to="/about"
                className="btn-primary text-lg font-semibold inline-block px-8 py-3 rounded-full shadow-lg"
              >
                {language === 'ar' ? 'تعرف علينا أكثر' : 'Learn More About Us'}
              </Link>
            </motion.div>
          </motion.div>

          {/* Cards Grid */}
          <div className="grid grid-cols-2 gap-4">
            {reasons.map((reason, index) => {
              const IconComponent = reason.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  viewport={{ once: true }}
                  className={`group relative ${reason.bgColor} rounded-xl p-6 text-center hover:shadow-lg transition-shadow`}
                >
                  {onDeleteReason && (
                    <DeleteButton onDelete={() => onDeleteReason(index)} size="sm" />
                  )}
                  <div className={`w-12 h-12 mx-auto mb-4 rounded-full bg-gradient-to-r ${reason.color} flex items-center justify-center`}>
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2 text-sm">
                    {isRTL ? reason.title : reason.title}
                  </h3>
                  <p className="text-gray-600 text-xs leading-relaxed">
                    {isRTL ? reason.description : reason.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs; 